import {useFormatCurrency, useTranslation} from '@aboutyou/i18n';
import {FadeTransition} from '@aboutyou/transitions';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  RotatingText,
  Text,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {CouponCard} from '../CouponCard/CouponCard';
import {CouponDeepLinkActivationProps} from './CouponDeepLinkActivation';
import {CouponDeepLinkActivationDesktopHeader} from './CouponDeepLinkActivationDesktopHeader';

export const CouponDeepLinkActivationContent: React.FC<
  Pick<
    CouponDeepLinkActivationProps,
    'status' | 'headline' | 'subline' | 'coupon' | 'couponReduction'
  > & {
    ConfettiAnimationComponent?: React.ReactNode;
  }
> = ({
  ConfettiAnimationComponent,
  coupon,
  couponReduction,
  headline,
  status,
  subline,
}) => {
  const {t} = useTranslation();
  const formatCurrency = useFormatCurrency();

  return (
    <StyledFadeTransition duration={300}>
      {ConfettiAnimationComponent}
      <CouponDeepLinkActivationDesktopHeader status={status} />
      <Content>
        <Headline
          textAlignment="center"
          data-testid="coupon-deeplink-activation-headline"
        >
          {headline ?? t('Just a second, your coupon’s getting ready!')}
        </Headline>
        {subline && <Subline textAlignment="center">{subline}</Subline>}

        {status === 'loading' && (
          <RotatingText
            size={process.env.STACK === 'mobile' ? 'px12' : 'px14'}
            color="Grey1"
            interval={2000}
            labels={[
              t('Warming up coupon engine.'),
              t('Unwrapping your discount.'),
              t('Getting set for savings.'),
            ]}
          />
        )}

        {couponReduction && (
          <CouponReduction>
            <span>{t('Your total savings')}</span>
            <span>
              {formatCurrency(
                couponReduction.amount,
                couponReduction.currencyCode,
              )}
            </span>
          </CouponReduction>
        )}

        {status === 'success' && coupon && (
          <StyledCouponCard coupon={coupon} isDisplayMode={true} />
        )}
      </Content>
    </StyledFadeTransition>
  );
};

const StyledFadeTransition = styled(FadeTransition)`
  position: relative;
  overflow: hidden;

  ${tabletAndUpLinaria(`
    width: min(700px, 80vw);
  `)}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 24px;
  gap: 16px;

  ${tabletAndUpLinaria(`
    padding: 80px 24px 48px 24px;
    gap: 24px;
  `)}
`;

const Headline = styled(Text)`
  color: ${COLORS.PrimaryCoupon};
  font-size: ${FONT_SIZES.px20};
  line-height: 1.2;
  white-space: pre-wrap;
  font-weight: ${FONT_WEIGHTS.mediumHeavy};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px24};
    font-weight: ${FONT_WEIGHTS.heavy};
    padding-inline: 80px;
  `)}
`;

const Subline = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  line-height: 1.5;
  padding-inline: 16px;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
    padding-inline: 80px;
  `)}
`;

const CouponReduction = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  background-color: ${COLORS.BackgroundCoupon};
  color: ${COLORS.PrimaryCoupon};
  font-size: ${FONT_SIZES.px14};
  padding: 8px 16px;
  margin-top: 16px;

  ${tabletAndUpLinaria(`
      width: 60%;
  `)}
`;

const StyledCouponCard = styled(CouponCard)`
  ${tabletAndUpLinaria(`
      min-width: 350px;
  `)}
`;
